export type Price = {
    display: number
    vatExcluded: number
    vatIncluded: number
    currency: object
}

export type ElementKey =
    | 'size'
    | 'sizeLabel'
    | 'appearance'
    | 'appearanceLabel'
    | 'sellable'
    | 'idea'
    | 'product'
    | 'productLookupId'
    | 'productType'
    | 'appearanceLink'

export type ElementItem = {
    href?: string
    id: string
    type: 'sprd:sellable' | 'sprd:product'
    properties: {
        key: ElementKey
        value: string
    }[]
    shop: {
        id: string
    }
}

export type BasketItem = {
    id: string
    description: string
    element: ElementItem
    price: Price
    priceItem: Price
    quantity: number
    discounts: object[]
}

export type BasketItemWithImage = BasketItem & {
    image: {
        src: string
    }
}

export type BasketLinkType = 'platformCheckout' | 'shopCheckout' | 'defaultCheckout'

export type BasketLink = {
    type: BasketLinkType
    href: string
}

export type BasketBase = {
    id: string
}

export type Basket = BasketBase & {
    basketItems: BasketItem[]
    priceItems: Price
    priceTotal: Price
    shipping: {
        price: Price
    }
    discounts: {
        price: Price
        type: string
    }[]
    links: BasketLink[]
}

export type BasketWithImages = Omit<Basket, 'basketItems'> & {
    basketItems: BasketItemWithImage[]
}

export type BasketResponse = {
    basket: Basket
}

export type BasketPayload = {
    basketItems: Pick<BasketItem, 'quantity' | 'element'>
}

export const getProperty = (
    properties: ElementItem['properties'],
    key: ElementKey
): string | undefined => {
    return properties.find((prop) => prop.key === key)?.value
}

export const mergeBasketItems = (
    basketItems: BasketPayload['basketItems'][]
): BasketPayload['basketItems'][] => {
    const basketElementMap = basketItems.reduce<Record<string, BasketPayload['basketItems']>>(
        (prev, current) => {
            if (current.element.type === 'sprd:product') {
                return {
                    ...prev,
                    [current.element.id]: {
                        quantity: current.quantity,
                        element: {
                            href: current.element.href,
                            id: current.element.id,
                            type: 'sprd:product',
                            properties: current.element.properties,
                            shop: {
                                id: current.element.shop.id,
                            },
                        },
                    },
                }
            }

            return {
                ...prev,
                [current.element.id]: {
                    quantity: (prev[current.element.id]?.quantity ?? 0) + current.quantity,
                    element: {
                        id: current.element.id,
                        type: 'sprd:sellable',
                        properties: current.element.properties,
                        shop: {
                            id: current.element.shop.id,
                        },
                    },
                },
            }
        },
        {}
    )

    return Object.values<BasketPayload['basketItems']>(basketElementMap).sort((a, b) =>
        a.element.id.localeCompare(b.element.id)
    )
}
